import { useState, useEffect } from 'react';
import { evaluationApi, studentApi, teacherApi, schoolYearApi } from '../lib/api';
import { usePermissions } from '../components/Layout';
import {
  Plus,
  Search,
  Filter,
  Calendar,
  ClipboardCheck,
  Star,
  BookOpen,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function EvaluationsPage() {
  const { canCreate } = usePermissions();
  const [evaluations, setEvaluations] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newEvaluation, setNewEvaluation] = useState({
    student_id: '',
    teacher_id: '',
    evaluation_type: 'daily',
    evaluation_date: new Date().toISOString().split('T')[0],
    memorization_score: 10,
    recitation_score: 10,
    discipline_score: 10,
    behavior_score: 10,
    notes: '',
    surah_evaluated: '',
    verse_start: '',
    verse_end: '',
  });

  // Liste des 114 sourates du Coran avec nombre de versets
  const surahs = [
    { id: 1, name: "Al-Fatiha (الفاتحة)", verses: 7 },
    { id: 2, name: "Al-Baqara (البقرة)", verses: 286 },
    { id: 3, name: "Aal-Imran (آل عمران)", verses: 200 },
    { id: 4, name: "An-Nisa (النساء)", verses: 176 },
    { id: 5, name: "Al-Ma'ida (المائدة)", verses: 120 },
    { id: 6, name: "Al-An'am (الأنعام)", verses: 165 },
    { id: 7, name: "Al-A'raf (الأعراف)", verses: 206 },
    { id: 8, name: "Al-Anfal (الأنفال)", verses: 75 },
    { id: 9, name: "At-Tawba (التوبة)", verses: 129 },
    { id: 10, name: "Yunus (يونس)", verses: 109 },
    { id: 11, name: "Hud (هود)", verses: 123 },
    { id: 12, name: "Yusuf (يوسف)", verses: 111 },
    { id: 13, name: "Ar-Ra'd (الرعد)", verses: 43 },
    { id: 14, name: "Ibrahim (إبراهيم)", verses: 52 },
    { id: 15, name: "Al-Hijr (الحجر)", verses: 99 },
    { id: 16, name: "An-Nahl (النحل)", verses: 128 },
    { id: 17, name: "Al-Isra (الإسراء)", verses: 111 },
    { id: 18, name: "Al-Kahf (الكهف)", verses: 110 },
    { id: 19, name: "Maryam (مريم)", verses: 98 },
    { id: 20, name: "Ta-Ha (طه)", verses: 135 },
    { id: 21, name: "Al-Anbiya (الأنبياء)", verses: 112 },
    { id: 22, name: "Al-Hajj (الحج)", verses: 78 },
    { id: 23, name: "Al-Mu'minun (المؤمنون)", verses: 118 },
    { id: 24, name: "An-Nur (النور)", verses: 64 },
    { id: 25, name: "Al-Furqan (الفرقان)", verses: 77 },
    { id: 26, name: "Ash-Shu'ara (الشعراء)", verses: 227 },
    { id: 27, name: "An-Naml (النمل)", verses: 93 },
    { id: 28, name: "Al-Qasas (القصص)", verses: 88 },
    { id: 29, name: "Al-Ankabut (العنكبوت)", verses: 69 },
    { id: 30, name: "Ar-Rum (الروم)", verses: 60 },
    { id: 31, name: "Luqman (لقمان)", verses: 34 },
    { id: 32, name: "As-Sajda (السجدة)", verses: 30 },
    { id: 33, name: "Al-Ahzab (الأحزاب)", verses: 73 },
    { id: 34, name: "Saba (سبأ)", verses: 54 },
    { id: 35, name: "Fatir (فاطر)", verses: 45 },
    { id: 36, name: "Ya-Sin (يس)", verses: 83 },
    { id: 37, name: "As-Saffat (الصافات)", verses: 182 },
    { id: 38, name: "Sad (ص)", verses: 88 },
    { id: 39, name: "Az-Zumar (الزمر)", verses: 75 },
    { id: 40, name: "Ghafir (غافر)", verses: 85 },
    { id: 41, name: "Fussilat (فصلت)", verses: 54 },
    { id: 42, name: "Ash-Shura (الشورى)", verses: 53 },
    { id: 43, name: "Az-Zukhruf (الزخرف)", verses: 89 },
    { id: 44, name: "Ad-Dukhan (الدخان)", verses: 59 },
    { id: 45, name: "Al-Jathiya (الجاثية)", verses: 37 },
    { id: 46, name: "Al-Ahqaf (الأحقاف)", verses: 35 },
    { id: 47, name: "Muhammad (محمد)", verses: 38 },
    { id: 48, name: "Al-Fath (الفتح)", verses: 29 },
    { id: 49, name: "Al-Hujurat (الحجرات)", verses: 18 },
    { id: 50, name: "Qaf (ق)", verses: 45 },
    { id: 51, name: "Adh-Dhariyat (الذاريات)", verses: 60 },
    { id: 52, name: "At-Tur (الطور)", verses: 49 },
    { id: 53, name: "An-Najm (النجم)", verses: 62 },
    { id: 54, name: "Al-Qamar (القمر)", verses: 55 },
    { id: 55, name: "Ar-Rahman (الرحمن)", verses: 78 },
    { id: 56, name: "Al-Waqi'a (الواقعة)", verses: 96 },
    { id: 57, name: "Al-Hadid (الحديد)", verses: 29 },
    { id: 58, name: "Al-Mujadila (المجادلة)", verses: 22 },
    { id: 59, name: "Al-Hashr (الحشر)", verses: 24 },
    { id: 60, name: "Al-Mumtahina (الممتحنة)", verses: 13 },
    { id: 61, name: "As-Saff (الصف)", verses: 14 },
    { id: 62, name: "Al-Jumu'a (الجمعة)", verses: 11 },
    { id: 63, name: "Al-Munafiqun (المنافقون)", verses: 11 },
    { id: 64, name: "At-Taghabun (التغابن)", verses: 18 },
    { id: 65, name: "At-Talaq (الطلاق)", verses: 12 },
    { id: 66, name: "At-Tahrim (التحريم)", verses: 12 },
    { id: 67, name: "Al-Mulk (الملك)", verses: 30 },
    { id: 68, name: "Al-Qalam (القلم)", verses: 52 },
    { id: 69, name: "Al-Haqqa (الحاقة)", verses: 52 },
    { id: 70, name: "Al-Ma'arij (المعارج)", verses: 44 },
    { id: 71, name: "Nuh (نوح)", verses: 28 },
    { id: 72, name: "Al-Jinn (الجن)", verses: 28 },
    { id: 73, name: "Al-Muzzammil (المزمل)", verses: 20 },
    { id: 74, name: "Al-Muddaththir (المدثر)", verses: 56 },
    { id: 75, name: "Al-Qiyama (القيامة)", verses: 40 },
    { id: 76, name: "Al-Insan (الإنسان)", verses: 31 },
    { id: 77, name: "Al-Mursalat (المرسلات)", verses: 50 },
    { id: 78, name: "An-Naba (النبأ)", verses: 40 },
    { id: 79, name: "An-Nazi'at (النازعات)", verses: 46 },
    { id: 80, name: "Abasa (عبس)", verses: 42 },
    { id: 81, name: "At-Takwir (التكوير)", verses: 29 },
    { id: 82, name: "Al-Infitar (الانفطار)", verses: 19 },
    { id: 83, name: "Al-Mutaffifin (المطففين)", verses: 36 },
    { id: 84, name: "Al-Inshiqaq (الانشقاق)", verses: 25 },
    { id: 85, name: "Al-Buruj (البروج)", verses: 22 },
    { id: 86, name: "At-Tariq (الطارق)", verses: 17 },
    { id: 87, name: "Al-A'la (الأعلى)", verses: 19 },
    { id: 88, name: "Al-Ghashiya (الغاشية)", verses: 26 },
    { id: 89, name: "Al-Fajr (الفجر)", verses: 30 },
    { id: 90, name: "Al-Balad (البلد)", verses: 20 },
    { id: 91, name: "Ash-Shams (الشمس)", verses: 15 },
    { id: 92, name: "Al-Layl (الليل)", verses: 21 },
    { id: 93, name: "Ad-Duha (الضحى)", verses: 11 },
    { id: 94, name: "Ash-Sharh (الشرح)", verses: 8 },
    { id: 95, name: "At-Tin (التين)", verses: 8 },
    { id: 96, name: "Al-Alaq (العلق)", verses: 19 },
    { id: 97, name: "Al-Qadr (القدر)", verses: 5 },
    { id: 98, name: "Al-Bayyina (البينة)", verses: 8 },
    { id: 99, name: "Az-Zalzala (الزلزلة)", verses: 8 },
    { id: 100, name: "Al-Adiyat (العاديات)", verses: 11 },
    { id: 101, name: "Al-Qari'a (القارعة)", verses: 11 },
    { id: 102, name: "At-Takathur (التكاثر)", verses: 8 },
    { id: 103, name: "Al-Asr (العصر)", verses: 3 },
    { id: 104, name: "Al-Humaza (الهمزة)", verses: 9 },
    { id: 105, name: "Al-Fil (الفيل)", verses: 5 },
    { id: 106, name: "Quraysh (قريش)", verses: 4 },
    { id: 107, name: "Al-Ma'un (الماعون)", verses: 7 },
    { id: 108, name: "Al-Kawthar (الكوثر)", verses: 3 },
    { id: 109, name: "Al-Kafirun (الكافرون)", verses: 6 },
    { id: 110, name: "An-Nasr (النصر)", verses: 3 },
    { id: 111, name: "Al-Masad (المسد)", verses: 5 },
    { id: 112, name: "Al-Ikhlas (الإخلاص)", verses: 4 },
    { id: 113, name: "Al-Falaq (الفلق)", verses: 5 },
    { id: 114, name: "An-Nas (الناس)", verses: 6 },
  ];

  const getSelectedSurah = () => surahs.find(s => s.id.toString() === newEvaluation.surah_evaluated);
  const selectedSurahVerses = getSelectedSurah()?.verses || 0;

  useEffect(() => {
    fetchData();
  }, [filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') {
        params.evaluation_type = filterType;
      }

      const [evalsRes, studentsRes, teachersRes, yearRes] = await Promise.all([
        evaluationApi.getAll(params),
        studentApi.getAll({ is_active: true }),
        teacherApi.getAll({ is_active: true }),
        schoolYearApi.getActive(),
      ]);

      setEvaluations(evalsRes.data);
      setStudents(studentsRes.data);
      setTeachers(teachersRes.data);
      setSchoolYear(yearRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvaluation = async (e) => {
    e.preventDefault();
    if (!newEvaluation.student_id || !newEvaluation.teacher_id) {
      toast.error('Veuillez sélectionner un élève et un enseignant');
      return;
    }

    setFormLoading(true);
    try {
      const surah = surahs.find(s => s.id.toString() === newEvaluation.surah_evaluated);
      const versesRange = newEvaluation.verse_start && newEvaluation.verse_end 
        ? `${newEvaluation.verse_start}-${newEvaluation.verse_end}`
        : '';
      
      await evaluationApi.create({
        ...newEvaluation,
        surah_evaluated: surah ? surah.name : '',
        verses_range: versesRange,
        school_year_id: schoolYear?.id || '',
      });
      toast.success('Évaluation enregistrée');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setNewEvaluation({
      student_id: '',
      teacher_id: '',
      evaluation_type: 'daily',
      evaluation_date: new Date().toISOString().split('T')[0],
      memorization_score: 10,
      recitation_score: 10,
      discipline_score: 10,
      behavior_score: 10,
      notes: '',
      surah_evaluated: '',
      verse_start: '',
      verse_end: '',
    });
  };

  const getEvaluationTypeInfo = (type) => {
    const types = {
      daily: { label: 'Journalière', class: 'bg-green-100 text-green-700' },
      weekly: { label: 'Hebdomadaire', class: 'bg-blue-100 text-blue-700' },
      monthly: { label: 'Mensuelle', class: 'bg-purple-100 text-purple-700' },
      trimestrial: { label: 'Trimestrielle', class: 'bg-amber-100 text-amber-700' },
      semestrial: { label: 'Semestrielle', class: 'bg-red-100 text-red-700' },
    };
    return types[type] || types.daily;
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Inconnu';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find((t) => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Inconnu';
  };

  const calculateAverage = (eval_) => {
    const scores = [
      eval_.memorization_score,
      eval_.recitation_score,
      eval_.discipline_score,
      eval_.behavior_score,
    ].filter((s) => s !== null && s !== undefined);
    
    if (scores.length === 0) return '-';
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  const stats = {
    total: evaluations.length,
    daily: evaluations.filter((e) => e.evaluation_type === 'daily').length,
    weekly: evaluations.filter((e) => e.evaluation_type === 'weekly').length,
    monthly: evaluations.filter((e) => e.evaluation_type === 'monthly').length,
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="evaluations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Évaluations</h1>
          <p className="text-muted-foreground text-sm">
            Gérez les évaluations coraniques des élèves
          </p>
        </div>
      </div>

      {/* Quick Links to Quranic Evaluations */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Évaluations Coraniques
          </CardTitle>
          <CardDescription>Accédez aux formulaires d'évaluation quotidienne, mensuelle et trimestrielle</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Button variant="outline" className="h-auto py-3 flex-col bg-white" asChild>
              <a href="/quranic-daily-eval">
                <Calendar className="w-5 h-5 mb-1 text-blue-500" />
                <span className="text-sm">Quotidienne</span>
                <span className="text-xs text-muted-foreground">Bind/Wett/Nafarr</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col bg-white" asChild>
              <a href="/monthly-eval">
                <Users className="w-5 h-5 mb-1 text-green-500" />
                <span className="text-sm">Mensuelle</span>
                <span className="text-xs text-muted-foreground">مراقبات شهرية</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col bg-white" asChild>
              <a href="/trimestrial-eval">
                <BookOpen className="w-5 h-5 mb-1 text-purple-500" />
                <span className="text-sm">Trimestrielle</span>
                <span className="text-xs text-muted-foreground">12 parties</span>
              </a>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col bg-white" asChild>
              <a href="/eval-dashboard">
                <ClipboardCheck className="w-5 h-5 mb-1 text-orange-500" />
                <span className="text-sm">Statistiques</span>
                <span className="text-xs text-muted-foreground">Tableau de bord</span>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Traditional Evaluations Section */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold">Évaluations avec notes</h2>
        {canCreate('evaluations') && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-evaluation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle évaluation
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouvelle évaluation</DialogTitle>
              <DialogDescription>Évaluation coranique (coranique)</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddEvaluation} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Élève *</Label>
                  <Select
                    value={newEvaluation.student_id}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, student_id: value })}
                  >
                    <SelectTrigger data-testid="eval-student">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Enseignant *</Label>
                  <Select
                    value={newEvaluation.teacher_id}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, teacher_id: value })}
                  >
                    <SelectTrigger data-testid="eval-teacher">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map((teacher) => (
                        <SelectItem key={teacher.id} value={teacher.id}>
                          {teacher.first_name} {teacher.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type d'évaluation</Label>
                  <Select
                    value={newEvaluation.evaluation_type}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, evaluation_type: value })}
                  >
                    <SelectTrigger data-testid="eval-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Journalière</SelectItem>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                      <SelectItem value="monthly">Mensuelle</SelectItem>
                      <SelectItem value="trimestrial">Trimestrielle</SelectItem>
                      <SelectItem value="semestrial">Semestrielle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newEvaluation.evaluation_date}
                    onChange={(e) => setNewEvaluation({ ...newEvaluation, evaluation_date: e.target.value })}
                    data-testid="eval-date"
                  />
                </div>
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-sm">Scores (sur 20)</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Mémorisation</Label>
                    <span className="text-sm font-medium w-12 text-right">{newEvaluation.memorization_score}/20</span>
                  </div>
                  <Slider
                    value={[newEvaluation.memorization_score]}
                    onValueChange={([value]) => setNewEvaluation({ ...newEvaluation, memorization_score: value })}
                    max={20}
                    step={1}
                    data-testid="memorization-slider"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Récitation (Tajwid)</Label>
                    <span className="text-sm font-medium w-12 text-right">{newEvaluation.recitation_score}/20</span>
                  </div>
                  <Slider
                    value={[newEvaluation.recitation_score]}
                    onValueChange={([value]) => setNewEvaluation({ ...newEvaluation, recitation_score: value })}
                    max={20}
                    step={1}
                    data-testid="recitation-slider"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Discipline</Label>
                    <span className="text-sm font-medium w-12 text-right">{newEvaluation.discipline_score}/20</span>
                  </div>
                  <Slider
                    value={[newEvaluation.discipline_score]}
                    onValueChange={([value]) => setNewEvaluation({ ...newEvaluation, discipline_score: value })}
                    max={20}
                    step={1}
                    data-testid="discipline-slider"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Comportement</Label>
                    <span className="text-sm font-medium w-12 text-right">{newEvaluation.behavior_score}/20</span>
                  </div>
                  <Slider
                    value={[newEvaluation.behavior_score]}
                    onValueChange={([value]) => setNewEvaluation({ ...newEvaluation, behavior_score: value })}
                    max={20}
                    step={1}
                    data-testid="behavior-slider"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Sourate évaluée</Label>
                  <Select
                    value={newEvaluation.surah_evaluated}
                    onValueChange={(value) => setNewEvaluation({ ...newEvaluation, surah_evaluated: value, verse_start: '', verse_end: '' })}
                  >
                    <SelectTrigger data-testid="eval-surah">
                      <SelectValue placeholder="Sélectionner une sourate" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {surahs.map((surah) => (
                        <SelectItem key={surah.id} value={surah.id.toString()}>
                          {surah.id}. {surah.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newEvaluation.surah_evaluated && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Verset début</Label>
                      <Select
                        value={newEvaluation.verse_start}
                        onValueChange={(value) => setNewEvaluation({ ...newEvaluation, verse_start: value })}
                      >
                        <SelectTrigger data-testid="eval-verse-start">
                          <SelectValue placeholder="Du verset" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {Array.from({ length: selectedSurahVerses }, (_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              Verset {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Verset fin</Label>
                      <Select
                        value={newEvaluation.verse_end}
                        onValueChange={(value) => setNewEvaluation({ ...newEvaluation, verse_end: value })}
                      >
                        <SelectTrigger data-testid="eval-verse-end">
                          <SelectValue placeholder="Au verset" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {Array.from({ length: selectedSurahVerses }, (_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              Verset {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes / Observations</Label>
                <Textarea
                  value={newEvaluation.notes}
                  onChange={(e) => setNewEvaluation({ ...newEvaluation, notes: e.target.value })}
                  placeholder="Observations sur l'élève..."
                  rows={3}
                  data-testid="eval-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="save-evaluation-btn">
                  {formLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.daily}</p>
              <p className="text-xs text-muted-foreground">Journalières</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.weekly}</p>
              <p className="text-xs text-muted-foreground">Hebdomadaires</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.monthly}</p>
              <p className="text-xs text-muted-foreground">Mensuelles</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="filter-eval-type">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="daily">Journalières</SelectItem>
                <SelectItem value="weekly">Hebdomadaires</SelectItem>
                <SelectItem value="monthly">Mensuelles</SelectItem>
                <SelectItem value="trimestrial">Trimestrielles</SelectItem>
                <SelectItem value="semestrial">Semestrielles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : evaluations.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Aucune évaluation enregistrée</p>
              <Button variant="outline" className="rounded-full" onClick={() => setShowAddDialog(true)}>
                Ajouter une évaluation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Élève</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead className="hidden lg:table-cell">Sourate</TableHead>
                  <TableHead className="text-center">Mémo</TableHead>
                  <TableHead className="text-center">Récit</TableHead>
                  <TableHead className="text-center hidden sm:table-cell">Discip</TableHead>
                  <TableHead className="text-center">Moy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {evaluations.map((eval_) => {
                  const typeInfo = getEvaluationTypeInfo(eval_.evaluation_type);
                  return (
                    <TableRow key={eval_.id} className="table-row-hover">
                      <TableCell>
                        <div>
                          <p className="font-medium">{getStudentName(eval_.student_id)}</p>
                          <p className="text-xs text-muted-foreground md:hidden">
                            {eval_.evaluation_date}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', typeInfo.class)}>
                          {typeInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {eval_.evaluation_date}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {eval_.surah_evaluated || '-'}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {eval_.memorization_score || '-'}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {eval_.recitation_score || '-'}
                      </TableCell>
                      <TableCell className="text-center font-medium hidden sm:table-cell">
                        {eval_.discipline_score || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-primary/10 text-primary">
                          {calculateAverage(eval_)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
